import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "@/lib/api";
import { Wordmark } from "@/components/Brand";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RoleBadges } from "@/components/RoleBadges";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Spinner } from "@/components/common";
import { PrivacyPromise } from "@/components/privacy/PrivacyPromise";
import { downloadVCard } from "@/lib/vcard";
import { initials } from "@/lib/format";
import { externalUrl, displayUrl } from "@/lib/url";
import { accentVars } from "@/lib/cardThemes";
import {
  Contact, Mail, Phone, Globe, Linkedin, MapPin, Briefcase, UserX, ArrowRight,
  Tag, Target, Sparkles,
} from "lucide-react";

function ContactRow({ icon: Icon, children, href, testId }) {
  const content = (
    <div className="flex items-center gap-3 rounded-lg border border-border bg-card px-3 py-2.5 text-sm">
      <Icon className="h-4 w-4 shrink-0 text-primary" />
      <span className="min-w-0 break-words text-foreground">{children}</span>
    </div>
  );
  return href ? (
    <a href={href} target="_blank" rel="noopener noreferrer" className="block transition-colors hover:border-primary/40" data-testid={testId}>{content}</a>
  ) : (
    <div data-testid={testId}>{content}</div>
  );
}

export default function PublicMember() {
  const { token } = useParams();
  const [m, setM] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    axios.get(`${API_BASE}/cards/public/${token}`)
      .then((r) => { setM(r.data); setStatus("ok"); })
      .catch(() => setStatus("notfound"));
  }, [token]);

  const biz = m?.business;
  const phone = m?.phone || biz?.phone;
  const email = m?.email || biz?.email;
  const website = m?.website || biz?.website;
  const linkedin = m?.linkedin || biz?.linkedin;
  const interests = m?.interests || [];

  return (
    <div className="fc-hero-bg fc-paper-noise flex min-h-screen flex-col items-center justify-center px-4 py-10">
      <div className="mb-6"><Wordmark withTagline /></div>
      <Card className="w-full max-w-lg overflow-hidden border-border bg-card shadow-[0_18px_50px_rgba(20,20,20,0.10)]" style={accentVars(m?.card_accent)}>
        {status === "ok" && m?.card_cover ? (
          <div className="relative h-24 w-full" data-testid="public-member-cover">
            <img src={m.card_cover} alt="" className="h-full w-full object-cover" />
            <span aria-hidden className="absolute inset-0" style={{ backgroundImage: "linear-gradient(to top, hsl(var(--card) / 0.5), transparent 55%)" }} />
          </div>
        ) : (
          <div aria-hidden className="h-1.5 w-full" style={{ backgroundImage: "linear-gradient(90deg, hsl(var(--fc-accent-2)), hsl(var(--fc-accent)))" }} />
        )}
        <CardContent className="p-7">
          {status === "loading" && <div className="flex justify-center py-10"><Spinner /></div>}

          {status === "notfound" && (
            <div className="py-8 text-center" data-testid="public-member-notfound">
              <UserX className="mx-auto h-10 w-10 text-muted-foreground" />
              <p className="mt-3 font-serif text-lg text-foreground">This card isn’t available</p>
              <p className="mt-1 text-sm text-muted-foreground">The link may have been reset or removed.</p>
              <Button asChild className="mt-4"><Link to="/">Visit Friday Conversations <ArrowRight className="ml-1 h-4 w-4" /></Link></Button>
            </div>
          )}

          {status === "ok" && m && (
            <div data-testid="public-member-card">
              <p className="text-xs font-semibold uppercase tracking-wider text-primary">Friday Conversations · Member</p>

              <div className="mt-3 flex items-center gap-4">
                <Avatar className="h-16 w-16">
                  <AvatarImage src={m.avatar || undefined} alt={m.name} />
                  <AvatarFallback className="bg-primary text-xl text-primary-foreground">{initials(m.name)}</AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <h1 className="truncate font-serif text-2xl font-semibold text-foreground" data-testid="public-member-name">{m.name}</h1>
                  <p className="truncate text-sm text-muted-foreground">{[m.title, m.company].filter(Boolean).join(" · ")}</p>
                  <RoleBadges roles={m.roles} is_owner={m.is_owner} showEligible={false} size="sm" className="mt-1.5" />
                </div>
              </div>

              {m.headline && (
                <p className="mt-4 flex items-start gap-2 rounded-lg bg-primary/5 px-3 py-2 text-sm font-medium text-foreground" data-testid="public-member-headline">
                  <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> {m.headline}
                </p>
              )}

              {m.bio && <p className="mt-4 text-sm leading-6 text-muted-foreground">{m.bio}</p>}

              {interests.length > 0 && (
                <div className="mt-4">
                  <p className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Interests</p>
                  <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="public-member-interests">
                    {interests.map((t) => (
                      <Badge key={t} variant="secondary" className="border border-border text-xs"><Tag className="mr-1 h-3 w-3" />{t}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {m.seeking && (
                <div className="mt-4">
                  <p className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Looking for</p>
                  <p className="mt-1 flex items-start gap-2 text-sm text-foreground" data-testid="public-member-looking-for">
                    <Target className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> {m.seeking}
                  </p>
                </div>
              )}

              <div className="mt-5 space-y-2">
                {email && <ContactRow icon={Mail} href={`mailto:${email}`} testId="public-member-email">{email}</ContactRow>}
                {phone && <ContactRow icon={Phone} href={`tel:${phone}`} testId="public-member-phone">{phone}</ContactRow>}
                {website && <ContactRow icon={Globe} href={externalUrl(website)} testId="public-member-website">{displayUrl(website)}</ContactRow>}
                {linkedin && <ContactRow icon={Linkedin} href={externalUrl(linkedin)} testId="public-member-linkedin">LinkedIn</ContactRow>}
                {m.location_text && <ContactRow icon={MapPin} testId="public-member-location">{m.location_text}</ContactRow>}
              </div>

              {biz && (biz.business_name || biz.tagline) && (
                <div className="mt-5 rounded-xl border border-border bg-secondary/40 p-4">
                  <div className="flex items-center gap-2 text-sm font-semibold text-foreground"><Briefcase className="h-4 w-4 text-primary" />{biz.business_name}</div>
                  {biz.tagline && <p className="mt-1 text-sm text-muted-foreground">{biz.tagline}</p>}
                  {biz.industry && <Badge variant="secondary" className="mt-2">{biz.industry}</Badge>}
                </div>
              )}

              <div className="mt-6 flex flex-col gap-2 sm:flex-row">
                <Button onClick={() => downloadVCard(m)} className="flex-1 bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="public-member-save-contact">
                  <Contact className="mr-2 h-4 w-4" /> Save contact
                </Button>
                <Button asChild variant="secondary" className="flex-1" data-testid="public-member-join">
                  <Link to={`/register?ref=${m.ref}`}>Join the group <ArrowRight className="ml-1 h-4 w-4" /></Link>
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      <div className="mt-4 w-full max-w-lg"><PrivacyPromise /></div>
    </div>
  );
}
